// The patient's view of their HME relationship — ACDPA-315 (leave) and D77/R-15 (invitation).
//
// Both objects arrive on `GET /me`; there is no dedicated read endpoint, by kernel design
// ("a client must read association state from the GET"). The verbs are separate routes.
//
// ⚠️ EVERY FIELD HERE IS OPTIONAL ON THE WIRE. Measured against dev on 2026-09-08: all three
// D68 routes return 404 there and `GET /me` carries neither field. So the absent case is the
// LIVE path today, not a defensive afterthought — every reader below must degrade to "no
// relationship" rather than assume presence.

import { kernelClient } from "./api";
import { AUTH_ENDPOINTS, ORG_SHARING_ENDPOINTS } from "./endpoints";
import type { ShareableOrg } from "./orgSharing";

/** 'org_created' — an organization typed this patient in or enrolled them. */
export const ORIGIN_ORG_CREATED = "org_created";
/** 'patient_initiated' — the patient shared from the app. */
export const ORIGIN_PATIENT_INITIATED = "patient_initiated";

/**
 * The ONE organization holding this patient's data. A single object, never a list: a patient
 * is capped at one live association however it arose (D68).
 */
export interface MyAssociation {
  org_id: number;
  org_name: string;
  location_name?: string | null;
  since?: string | null;
  /** 'org_created' | 'patient_initiated'. ⛔ Load-bearing — see `associationSummary`. */
  origin: string;
  /**
   * ⚠️ Structurally always true today; the kernel documents this rather than pretending
   * otherwise. Render the leave action from it, but do NOT build UI that depends on a false
   * case — nothing produces one.
   */
  can_leave: boolean;
}

/**
 * An organization asking this patient to CONFIRM an association.
 *
 * ⛔ NOT a variant of MyAssociation. An invitation grants NOTHING — no access until accepted —
 * so it carries `accept`/`decline` where an association carries `leave`.
 */
export interface MyInvitation {
  org_id: number;
  org_name: string;
  location_name?: string | null;
  invited_at?: string | null;
}

/**
 * F-600 (URS_6.9 AC5) — the patient's latest request to connect that an organization DECLINED,
 * with the reason it gave (null when none). Named like `MyInvitation`: `org_name` is the parent
 * organization when the request went to one of its locations, `location_name` that location.
 * ⭐ The kernel clears it on its own — when the patient asks again, or holds any live request or
 * association — so the app keeps no dismiss state. Absent on a kernel that predates F-600.
 */
export interface MyDeclinedShareRequest {
  org_id: number;
  org_name: string;
  location_name?: string | null;
  declined_at?: string | null;
  reason?: string | null;
}

export interface MyOrgRelationship {
  association: MyAssociation | null;
  invitation: MyInvitation | null;
  /** F-600 — optional so every existing two-field literal of this type stays valid. */
  declined_share_request?: MyDeclinedShareRequest | null;
}

/** The subject of a sentence about an org whose name the wire did not give us. */
export const UNNAMED_ORG = "An organization";

/**
 * "Org · Location", or whichever of the two the payload actually carries.
 *
 * ⛔ TOTAL — always a non-empty string. This was `o.location_name ? \`${o.org_name} · …\` :
 * o.org_name` over a parameter typed `{ org_name: string }`, so a payload missing the key
 * returned `undefined` and every caller interpolated the literal "undefined" into copy the
 * patient reads. The header comment on this file is explicit that EVERY FIELD IS OPTIONAL ON
 * THE WIRE, so the type was the only thing making that look safe.
 *
 * Joining the present parts (rather than branching on the location) also stops a blank org name
 * from rendering a dangling " · South Clinic".
 */
export const orgLabel = (
  o?: { org_name?: string | null; location_name?: string | null } | null,
): string =>
  [o?.org_name, o?.location_name]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" · ") || UNNAMED_ORG;

/**
 * Did an ORGANIZATION create this link, rather than the patient?
 *
 * Deliberately a positive test against the one known patient-initiated value rather than
 * `!== 'patient_initiated'`… no: it is the inverse, and the distinction matters. An UNKNOWN
 * origin must NOT be reported as org-created either — but it must also never be described to
 * the patient as something they chose. `associationSummary` therefore treats anything that is
 * not provably patient-initiated as neutral copy.
 */
export const isOrgCreated = (a: MyAssociation): boolean => a.origin === ORIGIN_ORG_CREATED;

/**
 * Which cards the screen shows.
 *
 * ⭐ `showAssociation` and `showInvitation` are INDEPENDENT booleans, not a 3-way state.
 * `GET /me` fills the two fields in separate `if` blocks and the kernel's own schema says
 * "BOTH CAN BE NON-NULL AT ONCE": an org that typed the patient in already holds their record
 * and may still be asking them to confirm it. Collapsing this into one exclusive state would
 * drop either the leave action or the accept/decline for precisely the org-created population
 * this feature exists to serve.
 */
export const relationshipCards = (
  rel: MyOrgRelationship | null | undefined,
): { showAssociation: boolean; showInvitation: boolean } => ({
  showAssociation: !!rel?.association,
  showInvitation: !!rel?.invitation,
});

/**
 * One line describing who holds the patient's record.
 *
 * ⛔ The org-created wording must not imply the patient chose the link — they did not; an
 * organization typed them in, and they are invisible through every share-based route. Anything
 * whose origin we cannot positively identify as patient-initiated gets the same neutral copy.
 */
export const associationSummary = (a: MyAssociation): string =>
  a.origin === ORIGIN_PATIENT_INITIATED
    ? `You shared your profile with ${orgLabel(a)}.`
    : `${orgLabel(a)} holds your Arc Connect record.`;

/**
 * The one sentence that asks the patient to answer an invitation.
 *
 * ⭐ Lives here, not in a component, because TWO surfaces say it: the Data Sharing card and the
 * home dashboard's "Actions required" card (B2-4 Task B). A second copy would drift the moment
 * either is reworded, and the patient would be told two different things about one invitation.
 */
export const invitationSummary = (
  inv?: { org_name?: string | null; location_name?: string | null } | null,
): string =>
  `${orgLabel(inv)} would like to connect with you. They won't see any of your data unless you accept.`;

/** F-600 (URS_6.9 AC5) — the one sentence telling the patient their request was declined. */
export const declinedRequestSummary = (
  d?: { org_name?: string | null; location_name?: string | null } | null,
): string => `${orgLabel(d)} declined your request to connect.`;

// ─────────────────────────────────────────────────────────────────────────────────────────────
// R-32 — the accept-time disclosure.
//
// ⛔ A patient holds ONE association at a time, so accepting an invitation from a DIFFERENT
// organization ENDS the incumbent's access. Nothing told them: Leave — which only REVOKES — is
// guarded by a confirm, while Accept — which revokes AND grants — fired on a single tap, and
// the last sentence the patient read before that tap ("they won't see any of your data unless
// you accept") answers what the INVITER gains, not what the incumbent loses.
//
// ⭐ Why this is ordinary now: until D193 an organization could only invite a patient it
// already held, so both cards named the same org and there was nothing to replace. D193 lets an
// org invite ANY existing app user, which makes "a different org on each card" the normal case.
//
// ⛔ Predicate and copy live HERE, not in either component, for the same reason
// `invitationSummary` does: two surfaces ask the question, and a second copy would drift — the
// patient would be told two different things about one invitation.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** The two relationship fields, however the caller happens to be holding them. */
type RelationshipFields = Pick<MyOrgRelationship, "association" | "invitation">;

const readableOrgId = (o?: { org_id?: number | null } | null): number | null => {
  const id = o?.org_id;
  return typeof id === "number" && isFinite(id) ? id : null;
};

/**
 * Would accepting the pending invitation END the current organization's access?
 *
 * ⛔ FALSE is the ordinary answer and must stay cheap: with no association, or with the SAME
 * org asking the patient to confirm a link it already holds, nothing is replaced and Accept
 * stays a single direct tap. A dialog in front of every accept would be noise, and noise is how
 * a warning that matters stops being read.
 *
 * ⛔ Also FALSE when either org_id is unreadable. Every field here is optional on the wire; with
 * an id missing, the same-org case (which must NOT warn) is indistinguishable from the replacing
 * one, and the warning would name an organization it could not read. Unprovable ⇒ unchanged
 * flow.
 */
export const acceptReplacesCurrentOrg = (rel?: RelationshipFields | null): boolean => {
  const held = readableOrgId(rel?.association);
  const inviting = readableOrgId(rel?.invitation);
  return held !== null && inviting !== null && held !== inviting;
};

/**
 * What the patient is told before that tap — or "" when there is nothing to disclose.
 *
 * ⚠️ "cannot add you back" is load-bearing and true: the kernel stamps the patient as the
 * deactivator of the link they end, and that stamp blocks the outgoing org from re-inviting
 * them. Getting back to them is something the PATIENT has to start. Do not soften it, and do
 * not overstate it further.
 *
 * ⛔ This is APPENDED to `invitationSummary`, never substituted for it — that sentence remains
 * true and answers a different question.
 */
export const replacementWarning = (rel?: RelationshipFields | null): string => {
  if (!acceptReplacesCurrentOrg(rel)) return "";
  const incoming = orgLabel(rel?.invitation);
  const incumbent = orgLabel(rel?.association);
  const incumbentOrg = rel?.association?.org_name?.trim() ?? "";
  const incomingOrg = rel?.invitation?.org_name?.trim() ?? "";
  // The second reference is shortened: "Acme · Downtown will no longer have access" after
  // naming it in full is a mouthful. ⛔ F24 (measured live 2026-09-15): when the two cards name
  // the SAME organization — two locations of one org — "<org> will no longer have access" is
  // FALSE, because the org keeps access through the incoming location. Shorten to the LOCATION
  // then, and fall back to the full label when there is no location to name. `orgLabel` already
  // guarantees that fallback is non-empty.
  const sameOrg = incumbentOrg !== "" && incumbentOrg === incomingOrg;
  const incumbentShort = sameOrg
    ? rel?.association?.location_name?.trim() || incumbent
    : incumbentOrg || incumbent;
  return (
    `${incoming} will replace ${incumbent} as the organization holding your record. ` +
    `${incumbentShort} will no longer have access, and cannot add you back — you'd need to ask them.`
  );
};

/**
 * F-492 (UAT, URS_6.8) — the organization rows that carry the X on "Who can see my data", which is
 * the patient's ONE way to leave an organization.
 *
 * ⭐ The directory's accepted shares, PLUS the organization `GET /me` says holds the patient when
 * the directory does not list it. That second arm is the org-created (or org-invited) link:
 * `GET /me/organizations` marks only shares the patient began as `is_shared`, so without it an
 * org-created patient would have no X — and, with the card's Leave button gone, no way out at all.
 *
 * ⛔ Never the same organization twice. The kernel caps a patient at one live association, so in
 * practice this is zero or one row; the directory's own rows keep their own shape.
 */
export const leavableOrgs = (
  connected: ShareableOrg[],
  association?: MyAssociation | null,
): ShareableOrg[] => {
  const id = readableOrgId(association);
  if (!association || id === null || connected.some((o) => o.org_id === id)) return connected;
  return [
    ...connected,
    {
      org_id: id,
      name: orgLabel(association),
      parent_id: null,
      is_shared: true,
      share_status: "accepted",
      shareable: false,
    },
  ];
};

/**
 * The Leave confirmation. ⛔ Not an agreement (see `LeaveOrgConfirm` — leaving REVOKES, and the
 * kernel records no wording version for it).
 *
 * ⛔ F-523 (UAT, URS_5.5, preview-101) — the tester had the "They cannot add you back
 * themselves — if you change your mind you'd need to share with them again, or accept a new
 * invitation from them" sentence removed from this popup. The two facts it stated (F16 — a new
 * invitation FROM THEM is a way back since the kernel's F15 ruling) are still true of the
 * backend; the copy just no longer says so here.
 */
export const leaveOrgMessage = (orgLabel: string): string =>
  `${orgLabel} will no longer be able to see your therapy data. Your past records stay in their ` +
  `records as required.`;

/**
 * F17 — the two "share with an organization" blurbs. Mode-neutral ON PURPOSE: nothing on the
 * wire says whether this deployment requires the organization to approve a share (kernel
 * `SHARE_REQUIRES_APPROVAL`, default ON since F23; neither `GET /me` nor `GET /me/organizations`
 * exposes it — contract C5). "Right away" was FALSE under approval mode, where the patient sees
 * "Awaiting approval" until the org acts; "once they confirm" is true under both. Do not add a
 * mode branch here until the kernel serves the flag.
 */
// F-524 (UAT, URS_6.8; supersedes F-468's wording): the exact sub-header copy UAT supplied.
// Pressing Share sends the request directly — there is no second confirmation popup.
export const SHARE_DIRECTORY_INTRO =
  "Search for your home medical equipment provider or clinic, select the location, and press " +
  "Share. Once they accept your request, they’ll be able to view your therapy data.";

// ─────────────────────────────────────────────────────────────────────────────────────────────
// B2-4 Task B — "the doorbell": the invitation on the HOME DASHBOARD.
//
// The dashboard already has a generic "Actions required" list. `ActionCardButtons` renders any
// item from its `type`/`status`/`message` and reads a `profile` only for the two blaze follow
// types, so surfacing an invitation there is an ADAPTER, not a new card, a new section or a
// second fetch. The two functions below are that adapter and its action router.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The `type` an adapted invitation carries.
 *
 * ⚠️ This exact string is already load-bearing in two committed tests (`ActionCardButtons.test`
 * and `requestTitle.test`) that pin the profile-less branch — it is not a new vocabulary word.
 */
export const ORG_INVITATION_ITEM_TYPE = "org_invitation";

/**
 * One invitation in the shape the dashboard's action list already renders.
 *
 * ⛔ NO `created_at`/`invited_at`. The card prints "N days ago" from `created_at`, and the
 * kernel's `invited_at` is NOT the age of this ask — re-inviting leaves it at the original
 * timestamp — so the dashboard would be stating something it cannot know. The card omits the
 * line when the field is absent.
 *
 * ⛔ NO terminal state. `status` is always "pending": the dashboard only ever knows "you are
 * being asked", never "you declined this".
 */
export interface OrgInvitationActionItem {
  /**
   * ⚠️ A STRING, deliberately. The dashboard drops an acted-on card with
   * `items.filter(i => i.id !== item.id)`, and blaze relationship ids are bare numbers — an
   * invitation reusing its `org_id` would silently drop the follow request with that id too.
   */
  id: string;
  org_id: number;
  type: typeof ORG_INVITATION_ITEM_TYPE;
  status: "pending";
  message: string;
  /** Always null: an invitation is about an ORG, and the card must reach its message branch. */
  profile: null;
}

/**
 * Adapt a kernel invitation into a dashboard action item — or nothing.
 *
 * ⛔ TOTAL. App.tsx's ErrorBoundary is the app's only one and renders `null` (TD-165), so a
 * throw while rendering one card blanks the WHOLE app; two commits on this branch exist solely
 * because a profile-less action item did exactly that (D124.2 / D143.5). An invitation is the
 * profile-less shape by construction, and every field on it is optional on the wire.
 *
 * Returns null when there is no usable `org_id`: both verbs are addressed BY org_id, so a card
 * without one would render buttons that can only post to `/me/organizations/undefined/…`. An
 * unactionable doorbell is worse than a silent one.
 */
export const invitationActionItem = (
  invitation?: MyInvitation | null,
): OrgInvitationActionItem | null => {
  const orgId = invitation?.org_id;
  if (typeof orgId !== "number" || !isFinite(orgId)) return null;
  return {
    id: `${ORG_INVITATION_ITEM_TYPE}:${orgId}`,
    org_id: orgId,
    type: ORG_INVITATION_ITEM_TYPE,
    status: "pending",
    message: invitationSummary(invitation),
    profile: null,
  };
};

/** The two verbs, as `useMyOrgRelationship` exposes them. */
export interface OrgInvitationActions {
  accept: (orgId: number) => Promise<boolean>;
  decline: (orgId: number) => Promise<boolean>;
}

/**
 * The org_id of an adapted org-invitation item, or null when the item is not one (a blaze
 * follow request, garbage, or an invitation that lost its org_id).
 *
 * ⛔ ONE reader, shared by the router and the R-32 gate. Two copies of "is this ours?" would
 * let the dashboard confirm a tap it then refuses to route, or route one it never confirmed.
 */
const orgInvitationItemOrgId = (item: unknown): number | null => {
  if (!item || typeof item !== "object") return null;
  const candidate = item as { type?: unknown; org_id?: unknown };
  if (candidate.type !== ORG_INVITATION_ITEM_TYPE) return null;
  return readableOrgId(candidate as { org_id?: number | null });
};

/**
 * Route a tapped action item to the org-invitation hook, if that is what it is.
 *
 * ⛔ THREE outcomes, not two:
 *   `null`  — not an org invitation; the caller must fall through to its blaze handler.
 *   `true`  — handled, and the kernel accepted it.
 *   `false` — handled, and it FAILED.
 *
 * The false case is why this is not a plain "handled" boolean. `useMyOrgRelationship` never
 * throws: it catches, sets its own toast and returns false. The home dashboard does not render
 * that toast and follows every action with an unconditional "Request accepted", so a two-state
 * return would tell the patient their invitation was accepted while the POST failed and the
 * card sat unchanged.
 *
 * ⛔ It takes the hook's actions as a parameter rather than calling the service directly: the
 * dashboard and the Data Sharing card must share ONE write path, including its reload. A second
 * `acceptOrgInvitation()` wired from HomeDashboard would be a forked write path that works the
 * day it is written and drifts after.
 */
export const handleOrgInvitationAction = async (
  item: unknown,
  verb: "accept" | "decline",
  actions: OrgInvitationActions,
): Promise<boolean | null> => {
  const orgId = orgInvitationItemOrgId(item);
  if (orgId === null) return null;
  return verb === "accept" ? actions.accept(orgId) : actions.decline(orgId);
};

/**
 * Does the DASHBOARD's tapped Accept need the R-32 confirm first?
 *
 * ⭐ Bottoms out in `acceptReplacesCurrentOrg`, the same predicate the Settings card asks —
 * this wrapper only adds what the dashboard has and Settings does not: an untyped action item
 * that may belong to blaze, or to an invitation that is no longer the pending one.
 */
export const acceptNeedsConfirmation = (
  item: unknown,
  rel?: RelationshipFields | null,
): boolean => {
  const orgId = orgInvitationItemOrgId(item);
  return (
    orgId !== null && orgId === readableOrgId(rel?.invitation) && acceptReplacesCurrentOrg(rel)
  );
};

/** Pull the relationship fields out of a `GET /me` body, tolerating their absence. */
export const readMyOrgRelationship = (me: unknown): MyOrgRelationship => {
  const body = (me ?? {}) as Partial<MyOrgRelationship>;
  return {
    association: body.association ?? null,
    invitation: body.invitation ?? null,
    declined_share_request: body.declined_share_request ?? null,
  };
};

/** Read the caller's current association + invitation from `GET /me`. */
export const getMyOrgRelationship = async (): Promise<MyOrgRelationship> => {
  const { data } = await kernelClient.get(AUTH_ENDPOINTS.KERNEL_ME);
  return readMyOrgRelationship(data);
};

/** Confirm an organization's invitation. Grants the org access. */
export const acceptOrgInvitation = async (orgId: number): Promise<void> => {
  await kernelClient.post(ORG_SHARING_ENDPOINTS.INVITATION_ACCEPT(orgId));
};

/** Decline an organization's invitation. Grants nothing, and nothing was granted before. */
export const declineOrgInvitation = async (orgId: number): Promise<void> => {
  await kernelClient.post(ORG_SHARING_ENDPOINTS.INVITATION_DECLINE(orgId));
};

/**
 * Leave the association — works for ANY origin, which is the point: it is the only route that
 * reaches an org-created link. Destructive and irreversible from the patient's side.
 */
export const leaveOrgAssociation = async (orgId: number): Promise<void> => {
  await kernelClient.delete(ORG_SHARING_ENDPOINTS.LEAVE_ASSOCIATION(orgId));
};
